import React from 'react';
import { Controller } from 'react-hook-form';
import {
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Box
} from '@mui/material';
import { Cancel as CancelIcon, Business as BusinessIcon } from '@mui/icons-material';

export const FormTextField = ({ 
  name, 
  control, 
  label, 
  placeholder, 
  helperText, 
  multiline = false, 
  rows = 1,
  type = 'text',
  maxLength,
  required = false,
  ...props 
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          label={required ? `${label} *` : label}
          placeholder={placeholder}
          multiline={multiline}
          rows={multiline ? rows : 1}
          type={type}
          margin="normal"
          error={!!error}
          helperText={error?.message || helperText}
          inputProps={maxLength ? { maxLength } : {}}
          sx={{
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.3s ease',
            },
            '& .MuiFormHelperText-root': {
              transition: 'all 0.3s ease',
              marginLeft: 0,
              color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)'
            },
            '& .MuiInputLabel-root': {
              '&.Mui-focused': {
                color: error ? '#d32f2f' : '#1976d2'
              }
            }
          }}
          {...props}
        />
      )}
    />
  );
};

export const FormSwitchField = ({ 
  name, 
  control, 
  label, 
  description, 
  color = 'primary',
  ...props 
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box>
          <FormControlLabel
            control={
              <Switch
                {...field}
                checked={field.value || false}
                color={color}
                {...props}
              />
            }
            label={label}
          />
          {(error?.message || description) && (
            <FormHelperText error={!!error} sx={{ mt: 0, ml: 0, color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)' }}>
              {error?.message || description}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
};

export const FormAutocompleteField = ({ 
  name, 
  control, 
  label, 
  placeholder,
  helperText,
  options = [],
  getOptionLabel = (option) => option.name || option.label || option,
  renderTags,
  multiple = true,
  required = false,
  ...props 
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
        // Get selected options for display (full objects)
        const getSelectedOptions = () => {
          if (!multiple) {
            if (typeof value === 'object') return value;
            return options.find(opt => opt.id === value) || null;
          }
        
          if (!Array.isArray(value) || !Array.isArray(options)) {
            return [];
          }
        
          // If value contains IDs, map them to full objects for display
          if (typeof value[0] !== 'object') {
            return options.filter(opt => value.includes(opt.id));
          }
        
          return value;
        };

        const selectedOptions = getSelectedOptions();

        return (
          <Autocomplete
            {...field}
            multiple={multiple}
            options={options}
            getOptionLabel={getOptionLabel}
            value={selectedOptions}
            onChange={(event, newValue) => {
              if (multiple) {
                // Always store only IDs in the form state
                const ids = Array.isArray(newValue) 
                  ? newValue.map(item => (typeof item === 'object' ? item.id : item))
                  : [];
                onChange(ids);
              } else {
                // For single selection, store the ID or null
                onChange(newValue ? (newValue.id || newValue) : null);
              }
            }}
            isOptionEqualToValue={(option, value) => {
              // Handle comparison for objects vs IDs
              if (typeof option === 'object' && typeof value === 'object') {
                return option.id === value.id;
              }
              if (typeof option === 'object') {
                return option.id === value;
              }
              if (typeof value === 'object') {
                return option === value.id;
              }
              return option === value;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={required ? `${label} *` : label}
                placeholder={placeholder}
                error={!!error}
                helperText={error?.message || helperText}
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.3s ease',
                  },
                  '& .MuiFormHelperText-root': {
                    transition: 'all 0.3s ease',
                    marginLeft: 0,
                    color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)'
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: error ? '#d32f2f' : '#1976d2'
                    }
                  }
                }}
              />
            )}
            renderTags={renderTags}
            {...props}
          />
        );
      }}
    />
  );
};

export const FormMultiSelectField = ({ 
  name, 
  control, 
  label, 
  options = [],
  renderValue,
  helperText,
  required = false,
  ...props 
}) => {
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    variant: 'menu',
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
        const currentValue = value || [];
        
        return (
          <FormControl fullWidth margin="normal" error={!!error}>
            <InputLabel id={`${name}-label`}>{required ? `${label} *` : label}</InputLabel>
            <Select
              {...field}
              labelId={`${name}-label`}
              multiple
              value={currentValue}
              onChange={(event) => {
                const selectedValue = event.target.value;
                let newValue = typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue;
                
                // Handle 'all' selection logic
                if (newValue.includes('all')) {
                  // If 'all' is selected, clear other selections
                  newValue = ['all'];
                } else if (currentValue.includes('all') && newValue.length > 0) {
                  // If 'all' was previously selected but now selecting specific industries
                  newValue = newValue.filter(v => v !== 'all');
                }
                
                onChange(newValue);
              }}
              input={<OutlinedInput label={required ? `${label} *` : label} />}
              MenuProps={MenuProps}
              renderValue={renderValue}
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.3s ease',
                }
              }}
              {...props}
            >
              {options.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  dense
                >
                  <Checkbox 
                    checked={currentValue.includes(option.value)}
                    size="small"
                  />
                  <ListItemText 
                    primary={option.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
             {(error?.message || helperText) && (
              <FormHelperText sx={{ 
                marginLeft: 0,
                transition: 'all 0.3s ease',
                color: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)'
              }}>
                {error?.message || helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export const renderCategoryTags = (value, getTagProps, options) => {
  if (!Array.isArray(value) || !Array.isArray(options)) {
    return [];
  }
  
  return value.map((category, index) => {
    // Handle both object and ID cases
    const categoryObj = typeof category === 'object' 
      ? category 
      : options.find(opt => opt.id === category);
    
    if (!categoryObj) return null;
    
    return (
      <Chip
        key={categoryObj.id || index}
        label={categoryObj.name || 'Unknown Category'}
        {...getTagProps({ index })}
        deleteIcon={<CancelIcon />}
        color="primary"
        variant="outlined"
      />
    );
  });
};

export const renderIndustryValue = (selected, options) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
    {selected.map((value) => {
      const industry = options.find(ind => ind.value === value);
      return (
        <Chip 
          key={value} 
          label={industry?.label || value}
          size="small"
          icon={<BusinessIcon />}
          variant="outlined"
          color="primary"
        />
      );
    })}
  </Box>
);

// import React from 'react';
// import { Controller } from 'react-hook-form';
// import {
//   TextField,
//   FormControlLabel,
//   Switch,
//   Autocomplete,
//   Chip,
//   Select,
//   MenuItem,
//   Checkbox,
//   ListItemText,
//   FormControl,
//   InputLabel,
//   OutlinedInput,
//   FormHelperText,
//   Box
// } from '@mui/material';
// import { Cancel as CancelIcon, Business as BusinessIcon } from '@mui/icons-material';

// export const FormTextField = ({ 
//   name, 
//   control, 
//   label, 
//   placeholder, 
//   helperText, 
//   multiline = false, 
//   rows = 1,
//   type = 'text',
//   maxLength,
//   ...props 
// }) => {
//   return (
//     <Controller
//       name={name}
//       control={control}
//       render={({ field, fieldState: { error } }) => (
//         <TextField
//           {...field}
//           fullWidth
//           label={label}
//           placeholder={placeholder}
//           multiline={multiline}
//           rows={multiline ? rows : 1}
//           type={type}
//           margin="normal"
//           error={!!error} // This will make border and label red
//           helperText={error?.message || helperText}
//           inputProps={maxLength ? { maxLength } : {}}
//           {...props}
//         />
//       )}
//     />
//   );
// };

// export const FormSwitchField = ({ 
//   name, 
//   control, 
//   label, 
//   description, 
//   color = 'primary',
//   ...props 
// }) => {
//   return (
//     <Controller
//       name={name}
//       control={control}
//       render={({ field }) => (
//         <Box>
//           <FormControlLabel
//             control={
//               <Switch
//                 {...field}
//                 checked={field.value || false}
//                 color={color}
//                 {...props}
//               />
//             }
//             label={label}
//           />
//           {description && (
//             <FormHelperText sx={{ mt: 0, ml: 0 }}>
//               {description}
//             </FormHelperText>
//           )}
//         </Box>
//       )}
//     />
//   );
// };

// export const FormAutocompleteField = ({ 
//   name, 
//   control, 
//   label, 
//   placeholder,
//   helperText,
//   options = [],
//   getOptionLabel = (option) => option.name || option.label || option,
//   renderTags,
//   multiple = true,
//   ...props 
// }) => {
//   return (
//     <Controller
//       name={name}
//       control={control}
//       render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
//         // Get selected options for display (full objects)
//         const getSelectedOptions = () => {
//           if (!multiple) {
//             if (typeof value === 'object') return value;
//             return options.find(opt => opt.id === value) || null;
//           }
        
//           if (!Array.isArray(value) || !Array.isArray(options)) {
//             return [];
//           }
        
//           // If value contains IDs, map them to full objects for display
//           if (typeof value[0] !== 'object') {
//             return options.filter(opt => value.includes(opt.id));
//           }
        
//           return value;
//         };

//         const selectedOptions = getSelectedOptions();

//         return (
//           <Autocomplete
//             {...field}
//             multiple={multiple}
//             options={options}
//             getOptionLabel={getOptionLabel}
//             value={selectedOptions}
//             onChange={(event, newValue) => {
//               if (multiple) {
//                 // Always store only IDs in the form state
//                 const ids = Array.isArray(newValue) 
//                   ? newValue.map(item => (typeof item === 'object' ? item.id : item))
//                   : [];
//                 onChange(ids);
//               } else {
//                 // For single selection, store the ID or null
//                 onChange(newValue ? (newValue.id || newValue) : null);
//               }
//             }}
//             isOptionEqualToValue={(option, value) => {
//               // Handle comparison for objects vs IDs
//               if (typeof option === 'object' && typeof value === 'object') {
//                 return option.id === value.id;
//               }
//               if (typeof option === 'object') {
//                 return option.id === value;
//               }
//               if (typeof value === 'object') {
//                 return option === value.id;
//               }
//               return option === value;
//             }}
//             renderInput={(params) => (
//               <TextField
//                 {...params}
//                 label={label}
//                 placeholder={placeholder}
//                 error={!!error}
//                 helperText={error?.message || helperText}
//                 margin="normal"
//               />
//             )}
//             renderTags={renderTags}
//             {...props}
//           />
//         );
//       }}
//     />
//   );
// };

// export const FormMultiSelectField = ({ 
//   name, 
//   control, 
//   label, 
//   options = [],
//   renderValue,
//   helperText,
//   ...props 
// }) => {
//   const ITEM_HEIGHT = 48;
//   const ITEM_PADDING_TOP = 8;
//   const MenuProps = {
//     PaperProps: {
//       style: {
//         maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
//         width: 250,
//       },
//     },
//     variant: 'menu',
//     getContentAnchorEl: null,
//     anchorOrigin: {
//       vertical: 'bottom',
//       horizontal: 'left',
//     },
//     transformOrigin: {
//       vertical: 'top',
//       horizontal: 'left',
//     },
//   };

//   return (
//     <Controller
//       name={name}
//       control={control}
//       render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
//         const currentValue = value || [];
        
//         return (
//           <FormControl fullWidth margin="normal" error={!!error}>
//             <InputLabel id={`${name}-label`}>{label}</InputLabel>
//             <Select
//               {...field}
//               labelId={`${name}-label`}
//               multiple
//               value={currentValue}
//               onChange={(event) => {
//                 const selectedValue = event.target.value;
//                 let newValue = typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue;
                
//                 // Handle 'all' selection logic
//                 if (newValue.includes('all')) {
//                   // If 'all' is selected, clear other selections
//                   newValue = ['all'];
//                 } else if (currentValue.includes('all') && newValue.length > 0) {
//                   // If 'all' was previously selected but now selecting specific industries
//                   newValue = newValue.filter(v => v !== 'all');
//                 }
                
//                 onChange(newValue);
//               }}
//               input={<OutlinedInput label={label} />}
//               MenuProps={MenuProps}
//               renderValue={renderValue}
//               {...props}
//             >
//               {options.map((option) => (
//                 <MenuItem 
//                   key={option.value} 
//                   value={option.value}
//                   dense
//                 >
//                   <Checkbox 
//                     checked={currentValue.includes(option.value)}
//                     size="small"
//                   />
//                   <ListItemText 
//                     primary={option.label}
//                     primaryTypographyProps={{ variant: 'body2' }}
//                   />
//                 </MenuItem>
//               ))}
//             </Select>
//              {(error?.message || helperText) && (
//               <FormHelperText error={!!error}>
//                 {error?.message || helperText}
//               </FormHelperText>
//             )}
//           </FormControl>
//         );
//       }}
//     />
//   );
// };

// export const renderCategoryTags = (value, getTagProps, options) => {
//   if (!Array.isArray(value) || !Array.isArray(options)) {
//     return [];
//   }
  
//   return value.map((category, index) => {
//     // Handle both object and ID cases
//     const categoryObj = typeof category === 'object' 
//       ? category 
//       : options.find(opt => opt.id === category);
    
//     if (!categoryObj) return null;
    
//     return (
//       <Chip
//         key={categoryObj.id || index}
//         label={categoryObj.name || 'Unknown Category'}
//         {...getTagProps({ index })}
//         deleteIcon={<CancelIcon />}
//         color="primary"
//         variant="outlined"
//       />
//     );
//   });
// };

// export const renderIndustryValue = (selected, options) => (
//   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
//     {selected.map((value) => {
//       const industry = options.find(ind => ind.value === value);
//       return (
//         <Chip 
//           key={value} 
//           label={industry?.label || value}
//           size="small"
//           icon={<BusinessIcon />}
//           variant="outlined"
//           color="primary"
//         />
//       );
//     })}
//   </Box>
// );
